import { defineStore } from 'pinia';
import { v4 as uuid } from 'uuid';
import * as store from '@/lib/constants/manuscripts/store';
import { MANUSCRIPT_V1_API_URL_PREFIX as URL_PREFIX } from '@/lib/constants/manuscripts/api';
import {
  useUserStore,
} from '@/stores';
import useImportQueueStore from '@/stores/manuscripts/importQueue';

const useImportStore = defineStore('manuscript-import', {
  state: () => ({
    importItems: [],
    isImporting: false,
    guidInputText: null,
    fileImportAcceptTypes: ['.docx'],
    flexBoxes: {
      guidInput: {
        heightPct: 50,
      },
      importList: {
        heightPct: 50,
      },
    },
    confirmNavigateDialog: null,
  }),
  getters: {
    showImportList() {
      return !!this.importItems?.length;
    },
    flexBoxHeights() {
      return {
        guidInput: {
          heightPct: this.showImportList
            ? this.flexBoxes.guidInput.heightPct
            : 100,
        },
        importList: {
          heightPct: this.flexBoxes.importList.heightPct,
        },
      };
    },
    guidImportItems: (state) => state.importItems.filter(i => !!i.guid),
    fileImportItems: (state) => state.importItems.filter(i => !!i.file),
    canImportItems() {
      return this.hasImportItems
        && !this.isValidating
        && !this.isImporting
        && !this.hasPreventImportErrors;
    },
    hasErrors() {
      return this.importItems.some((i) => i.errors?.length);
    },
    hasGuidImportErrors() {
      return this.importItems.some((i) => {
        const foundGuidError = i.errors.find(
          (error) => store.GUID_IMPORT_ERRORS.includes(error),
        );
        return !!foundGuidError;
      });
    },
    hasPreventImportErrors() {
      return this.importItems.some((i) => {
        const foundPreventImportError = i.errors.find(
          (error) => store.PREVENT_IMPORT_ERRORS.includes(error),
        );
        return !!foundPreventImportError;
      });
    },
    canCancelImport() {
      return this.hasImportItems
        && !this.isValidating
        && !this.isImporting;
    },
    hasImportItems() {
      return !!this.importItems.length;
    },
    isValidating() {
      return this.importItems.some((i) => i.isValidating);
    },
  },
  actions: {
    [store.SET_CONFIRM_NAVIGATE_DIALOG](dialog) {
      this.confirmNavigateDialog = dialog;
    },
    [store.UPDATE_FLEX_BOXES](flexBoxes) {
      this.flexBoxes = flexBoxes;
    },
    [store.CLEAR_IMPORT_DATA]({ ids } = {}) {
      if (!ids) {
        this.importItems = [];
      } else {
        this.importItems = this.importItems.filter((i) => !ids.includes(i.id));
      }

      this.guidInputText = '';
    },
    [store.SET_GUID_IMPORT_TEXT](value) {
      this.guidInputText = value;
    },
    [store.REMOVE_IMPORT_ITEM](importItem) {
      const index = this.importItems.findIndex(i => i.id === importItem.id);
      if (index !== -1) {
        this.importItems.splice(index, 1);
      }
    },
    [store.ADD_IMPORT_ITEMS](importItems) {
      importItems.reverse().forEach((item) => {
        if (item.guid) {
          if (!this.importItems.find(i => i.guid === item.guid)) {
            this.importItems.unshift(item);
          }
        }
        if (item.file) {
          if (!this.importItems.find(i => i.file?.name === item.file.name)) {
            this.importItems.unshift(item);
          }
        }
      });
    },
    [store.UPDATE_IMPORT_ITEMS](updateItems) {
      const updatedItems = [];

      updateItems.forEach((updateItem) => {
        const itemToUpdate = this.importItems.find(i => i.id === updateItem.id);
        let updated = false;

        if (itemToUpdate) {
          Object.keys(updateItem).forEach((key) => {
            const updatedValue = updateItem[key];
            if (updatedValue !== itemToUpdate[key]) {
              itemToUpdate[key] = updatedValue;
              updated = true;
            }
          });
        }

        if (updated) {
          updatedItems.push(itemToUpdate);
        }
      });

      return updatedItems;
    },
    [store.UPDATE_AND_VALIDATE_IMPORT_ITEMS](updateItems) {
      const updatedItems = this[store.UPDATE_IMPORT_ITEMS](updateItems);
      this[store.VALIDATE_IMPORT_ITEMS](updatedItems);
    },
    [store.ADD_GUID_IMPORT_ITEMS](guids) {
      const importItems = guids.map((guid) => ({
        id: uuid(),
        guid,
        isGuid: true,
        errors: [],
        isValidating: false,
      }));

      this[store.ADD_IMPORT_ITEMS](importItems);

      this[store.VALIDATE_IMPORT_ITEMS](importItems);
    },
    [store.ADD_FILE_IMPORT_ITEMS](files) {
      const importItems = files.map((file) => ({
        id: uuid(),
        fileName: file.name,
        file,
        isFile: true,
        errors: [],
        isValidating: false,
      }));

      this[store.ADD_IMPORT_ITEMS](importItems);

      this[store.VALIDATE_IMPORT_ITEMS](importItems);
    },
    async [store.ADD_IMPORT_ITEMS_TO_QUEUE]() {
      if (!this.canImportItems) {
        return;
      }

      try {
        this.isImporting = true;

        const userStore = useUserStore();
        const guids = this.guidImportItems.map((i) => i.guid);
        const hasGuids = !!guids.length;
        const hasFiles = !!this.fileImportItems.length;
        let manuscriptsImported = false;
        let guidRequest;
        let fileRequest;

        if (hasGuids) {
          const path = `${URL_PREFIX}/imports/studio`;
          const reqBody = {
            ids: guids,
            // TODO: wire this up
            product_id: '0253faf8-c8e4-468f-afb9-10b816629965',
          };

          guidRequest = this.appApi(path, {
            method: 'POST',
            body: reqBody,
            headers: {
              'X-Token': userStore.user.token,
            },
          });
        }

        if (hasFiles) {
          const formData = new FormData();

          this.fileImportItems.forEach((item) => {
            const fileName = item.fileName.trim() || item.file.name;

            formData.append('manuscript_files', item.file, fileName);
          });

          const path = `${URL_PREFIX}/imports/document`;

          fileRequest = this.appApi(path, {
            method: 'POST',
            body: formData,
            headers: {
              'X-Token': userStore.user.token,
            },
          }, false);
        }

        const handleGuidResponse = (guidResponse) => {
          try {
            const { errored } = guidResponse?.data;

            const updateItems = this.guidImportItems
              .filter((i) => errored.includes(i.guid))
              .map((i) => ({ id: i.id, errors: [store.ERROR_FAILED_TO_IMPORT] }));

            this[store.UPDATE_IMPORT_ITEMS](updateItems);

            const clearIds = this.guidImportItems
              .filter((i) => !errored.includes(i.guid))
              .map((i) => i.id);

            this[store.CLEAR_IMPORT_DATA]({ ids: clearIds });

            if (clearIds.length) {
              manuscriptsImported = true;
            }
          } catch {
            const updateItems = this.guidImportItems.map(
              (i) => ({ id: i.id, errors: [store.ERROR_FAILED_TO_IMPORT] }),
            );
            this[store.UPDATE_IMPORT_ITEMS](updateItems);
          }
        };

        const handleFileResponse = (fileResponse) => {
          try {
            const { errored } = fileResponse?.data;

            const updateItems = this.fileImportItems
              .filter((i) => errored.includes(i.fileName))
              .map((i) => ({ id: i.id, errors: [store.ERROR_FAILED_TO_IMPORT] }));

            this[store.UPDATE_IMPORT_ITEMS](updateItems);

            const clearIds = this.fileImportItems
              .filter((i) => !errored.includes(i.fileName))
              .map((i) => i.id);

            this[store.CLEAR_IMPORT_DATA]({ ids: clearIds });

            if (clearIds.length) {
              manuscriptsImported = true;
            }
          } catch {
            const updateItems = this.fileImportItems.map(
              (i) => ({ id: i.id, errors: [store.ERROR_FAILED_TO_IMPORT] }),
            );
            this[store.UPDATE_IMPORT_ITEMS](updateItems);
          }
        };

        if (hasGuids) {
          try {
            const guidResponse = await guidRequest;
            handleGuidResponse(guidResponse);
          } catch (error) {
            handleGuidResponse(error.response);
          }
        }

        if (hasFiles) {
          try {
            const fileResponse = await fileRequest;
            handleFileResponse(fileResponse);
          } catch (error) {
            handleFileResponse(error.response);
          }
        }

        if (manuscriptsImported) {
          const importQueue = useImportQueueStore();

          importQueue[store.SET_QUEUE_SORT]({
            direction: 'desc',
            field: store.SORT_FIELD_DATE_ADDED,
          });

          await importQueue[store.GET_QUEUE_ITEMS]();
        }
      } finally {
        this.isImporting = false;
      }
    },
    async [store.VALIDATE_IMPORT_ITEMS](items) {
      const guidItems = items.filter((i) => ('guid' in i));
      const fileItems = items.filter((i) => ('file' in i));

      if (fileItems.length) {
        const updateItems = fileItems.map((item) => {
          const updateItem = {
            id: item.id,
            errors: [],
          };

          const extList = this.fileImportAcceptTypes
            .map((t) => [
              `\\${t.toLocaleLowerCase()}`,
              `\\${t.toUpperCase()}`,
            ])
            .flat()
            .join('|');
          const extGroup = `(${extList})$`;
          const fileExtensionExp = new RegExp(extGroup);
          const fileNameExp = new RegExp(`^.+${extGroup}`);

          if (!fileExtensionExp.test(item.fileName)) {
            updateItem.errors.push(store.ERROR_INVALID_FILE_EXTENSION);
          }

          if (!fileNameExp.test(item.fileName)) {
            updateItem.errors.push(store.ERROR_INVALID_FILENAME);
          }

          return updateItem;
        });

        this[store.UPDATE_IMPORT_ITEMS](updateItems);
      }

      if (guidItems.length) {
        try {
          this[store.UPDATE_IMPORT_ITEMS](
            guidItems.map((i) => ({ id: i.id, isValidating: true, errors: [] })),
          );

          const userStore = useUserStore();
          const guids = guidItems.map((i) => i.guid);
          const path = `${URL_PREFIX}/imports/studio/validate`;

          const {
            data: {
              valid_ids: validIds,
              invalid_format_ids: invalidFormatIds,
            },
          } = await this.appApi(path, {
            method: 'POST',
            body: guids,
            headers: {
              'X-Token': userStore.user.token,
            },
          });

          const updateItems = guidItems.map((item) => {
            const updateItem = {
              id: item.id,
              errors: [],
            };

            if (invalidFormatIds.includes(item.guid)) {
              updateItem.errors.push(store.ERROR_INVALID_GUID);
            }

            if (!validIds.includes(item.guid)) {
              updateItem.errors.push(store.ERROR_INVALID_POINTER_GUID);
            }

            return updateItem;
          });

          this[store.UPDATE_IMPORT_ITEMS](updateItems);
        } catch {
          this[store.UPDATE_IMPORT_ITEMS](
            guidItems.map((i) => ({ id: i.id, errors: [store.ERROR_VALIDATION_FAILED] })),
          );
        } finally {
          this[store.UPDATE_IMPORT_ITEMS](
            guidItems.map((i) => ({ id: i.id, isValidating: false })),
          );
        }
      }
    },
  },
});

export default useImportStore;
